import React, { useMemo, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Center,
  Spinner,
  Text,
  Flex,
  Image,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import MyDiv from "./customExpandTable.style";

// ---------------- Column Definition ----------------
export interface ColumnProps<T> {
  field?: keyof T | string;
  header?: React.ReactNode;
  body?: (rowData: T, index: number) => React.ReactNode;
  sortable?: boolean;
  textAlign?: "left" | "center" | "right";
}

export function Column<T>(_props: ColumnProps<T>) {
  return null;
}

// ---------------- Table Props ----------------
interface CustomExpandTableProps<T> {
  value: T[];
  columns?: ColumnProps<T>[];
  children?: React.ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  emptyImage?: string;
  headerBg?: string;
  headerTextColor?: string;
  striped?: boolean;

  rowKey: string;
  renderExpandedContent: (row: T) => React.ReactNode;
  autoCloseExpand?: boolean;
  getRowBgColor?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
}

// ---------------- Main Component ----------------
export function CustomExpandTable<T extends Record<string, any>>({
  value,
  columns,
  children,
  loading = false,
  emptyMessage = "No data found",
  emptyImage = "https://cdn-icons-png.flaticon.com/512/4076/4076500.png",
  headerBg = "#F9FAFB",
  headerTextColor = "#667085",
  striped = true,
  rowKey,
  renderExpandedContent,
  autoCloseExpand = false,
  getRowBgColor,
  onRowClick,
}: CustomExpandTableProps<T>) {
  // Resolve columns
  const resolvedColumns: ColumnProps<T>[] = useMemo(() => {
    if (columns && columns.length) return columns;

    return React.Children.toArray(children)
      .filter(
        (child): child is React.ReactElement<ColumnProps<T>> =>
          React.isValidElement(child) && child.type === Column
      )
      .map((col) => col.props);
  }, [columns, children]);

  // Sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  // Expand state
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const toggleExpand = (row: T) => {
    const rowId = row[rowKey];

    if (rowId == null) return;

    setExpandedRowId((prev) =>
      prev === rowId ? null : rowId
    );
  };

  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder) return value;

    return [...value].sort((a, b) => {
      const valA = a[sortField as string];
      const valB = b[sortField as string];

      if (valA == null || valB == null) return 0;
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;

      return 0;
    });
  }, [value, sortField, sortOrder]);

  const handleSort = (field?: string) => {
    if (!field) return;

    if (sortField === field) {
      setSortOrder(
        sortOrder === "asc"
          ? "desc"
          : sortOrder === "desc"
            ? null
            : "asc"
      );
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <MyDiv>
      <Box position="relative" minH="360px" bg="transparent">
        {loading ? (
          <Center position="absolute" inset={0}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              color="#0052CC"
              size="xl"
            />
          </Center>
        ) : (
          <TableContainer
            borderRadius="12px"
            overflowX="auto"
            boxShadow="sm"
            mt={3}
            border="1px solid #edeeee"
          >
            {!loading && value.length === 0 ? (
              <Flex
                direction="column"
                justify="center"
                align="center"
                h="200px"
                bg="white"
              >
                <Text
                  className="font-poppins text_md text_semibold"
                  color="gray.500"
                >
                  {emptyMessage}
                </Text>
              </Flex>
            ) : (
              <Table variant="simple" size="sm">
                {/* Header */}
                <Thead
                  bg={headerBg}
                  className="table_header font-poppins text_medium text_sm"
                >
                  <Tr className="font-poppins text_regular text_md">
                    {resolvedColumns.map((col, idx) => (
                      <Th
                        className="font-poppins text_xsm text_medium"
                        key={idx}
                        fontSize="14px"
                        color={headerTextColor}
                        cursor={col.sortable ? "pointer" : "default"}
                        onClick={() =>
                          col.sortable &&
                          handleSort(col.field as string)
                        }
                      >
                        <Flex
                          align="center"
                          gap={1}
                          justify={
                            col.textAlign === "center"
                              ? "center"
                              : col.textAlign === "right"
                                ? "flex-end"
                                : "flex-start"
                          }
                        >
                          {col.header}

                          {col.sortable &&
                            sortField === col.field &&
                            (sortOrder === "asc" ? (
                              <TriangleUpIcon boxSize={3} />
                            ) : sortOrder === "desc" ? (
                              <TriangleDownIcon boxSize={3} />
                            ) : null)}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                </Thead>

                {/* Body */}
                <Tbody>
                  {sortedData.map((row, rowIndex) => {
                    const rowId = row[rowKey];
                    const isExpanded = expandedRowId === rowId;

                    return (
                      <React.Fragment key={rowIndex}>
                        <Tr
                          id={rowId !== undefined ? `lead-row-${rowId}` : undefined}
                          bg={getRowBgColor ? (getRowBgColor(row) || "#FFFFFF") : "#FFFFFF"}
                          _hover={{
                            bg: getRowBgColor && getRowBgColor(row)
                              ? getRowBgColor(row)
                              : striped
                                ? "#F9FAFB"
                                : "transparent",
                          }}
                          onClick={() => onRowClick && onRowClick(row)}
                          cursor={onRowClick ? "pointer" : "default"}
                        >
                          {resolvedColumns.map((col, colIndex) => (
                            <Td
                              key={colIndex}
                              whiteSpace="nowrap"
                              className="font-poppins text_sm text_medium"
                              fontSize="14px"
                              color="#1D2939"
                              textAlign={col.textAlign || "left"}
                            >
                              {col.body
                                ? col.body(
                                  {
                                    ...row,
                                    toggleExpand: () => toggleExpand(row),

                                    expandRow: () => {
                                      setExpandedRowId(row[rowKey]);
                                    },

                                    closeExpand: () => {
                                      setExpandedRowId(null);
                                    },

                                    isExpanded,
                                  } as T,
                                  rowIndex
                                )
                                : (row as any)[col.field as string]}
                            </Td>
                          ))}
                        </Tr>

                        {isExpanded && (
                          <Tr>
                            <Td
                              colSpan={resolvedColumns.length}
                              bg="#F9FAFB"
                              p={4}
                            >
                              {renderExpandedContent({
                                ...row,
                                closeExpand: () => setExpandedRowId(null),
                                isExpanded,
                              } as T)}
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>
            )}
          </TableContainer>
        )}
      </Box>
    </MyDiv>
  );
}

export default CustomExpandTable;